import { useEffect, useState } from 'react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { toast } from 'sonner';
import { Loader2, Pencil, Trash2, Plus, Upload } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface BoardMember {
  id: string;
  name: { ar: string; en: string };
  title: { ar: string; en: string };
  credentials: { ar: string; en: string };
  image: string;
  order: number;
}

export function BoardMembersSection() {
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState<BoardMember | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: { ar: '', en: '' },
    title: { ar: '', en: '' },
    credentials: { ar: '', en: '' },
    image: '',
    order: 0
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const membersQuery = query(
        collection(db, 'landing_board_members'),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(membersQuery);

      const membersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BoardMember[];

      setMembers(membersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching board members:', error);
      toast.error('حدث خطأ أثناء تحميل أعضاء مجلس الإدارة');
      setLoading(false);
    }
  };

  const handleEdit = (member: BoardMember) => {
    setEditingMember(member);
    setFormData({
      name: typeof member.name === 'string' ? { ar: member.name, en: '' } : member.name,
      title: typeof member.title === 'string' ? { ar: member.title, en: '' } : member.title,
      credentials: typeof member.credentials === 'string' ? { ar: member.credentials, en: '' } : member.credentials,
      image: member.image,
      order: member.order
    });
    setIsEditModalOpen(true);
  };

  const handleAdd = () => {
    setFormData({
      name: { ar: '', en: '' },
      title: { ar: '', en: '' },
      credentials: { ar: '', en: '' },
      image: '',
      order: members.length + 1
    });
    setIsAddModalOpen(true);
  };

  const handleSave = async () => {
    if (!editingMember) return;

    try {
      await updateDoc(doc(db, 'landing_board_members', editingMember.id), {
        name: formData.name,
        title: formData.title,
        credentials: formData.credentials,
        image: formData.image,
        order: formData.order,
        updatedAt: new Date()
      });

      toast.success('تم تحديث العضو بنجاح');
      setIsEditModalOpen(false);
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error('حدث خطأ أثناء تحديث العضو');
    }
  };

  const handleAddMember = async () => {
    try {
      await addDoc(collection(db, 'landing_board_members'), {
        name: formData.name,
        title: formData.title,
        credentials: formData.credentials,
        image: formData.image,
        order: formData.order,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      toast.success('تم إضافة العضو بنجاح');
      setIsAddModalOpen(false);
      fetchMembers();
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error('حدث خطأ أثناء إضافة العضو');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'landing_board_members', id));
      toast.success('تم حذف العضو بنجاح');
      setShowDeleteConfirm(null);
      fetchMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('حدث خطأ أثناء حذف العضو');
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">أعضاء مجلس الإدارة</h3>
          <Button onClick={handleAdd} size="sm">
            <Plus className="h-4 w-4 ml-2" />
            إضافة عضو
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map(member => (
            <div
              key={member.id}
              className="bg-secondary/50 rounded-lg p-4 hover:bg-secondary/70 transition-colors"
            >
              <div className="flex items-start gap-4">
                <img
                  src={member.image || 'https://via.placeholder.com/80'}
                  alt={typeof member.name === 'string' ? member.name : member.name.ar}
                  className="w-20 h-20 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-lg mb-1 truncate">{typeof member.name === 'string' ? member.name : member.name.ar}</h4>
                  <p className="text-sm text-muted-foreground mb-1">{typeof member.title === 'string' ? member.title : member.title.ar}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {typeof member.credentials === 'string' ? member.credentials : member.credentials.ar}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(member)}
                  className="flex-1"
                >
                  <Pencil className="h-4 w-4 ml-2" />
                  تعديل
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(member.id)}
                  className="flex-1 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4 ml-2" />
                  حذف
                </Button>
              </div>
            </div>
          ))}
        </div>

        {members.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>لا يوجد أعضاء في مجلس الإدارة</p>
            <Button onClick={handleAdd} className="mt-4">
              إضافة أول عضو
            </Button>
          </div>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">تعديل عضو مجلس الإدارة</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label className="text-base font-semibold">الاسم / Name</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="name-ar"
                    value={formData.name.ar}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ar: e.target.value } })}
                    dir="rtl"
                    placeholder="د. عبدالله السعيد"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="name-en"
                    value={formData.name.en}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                    dir="ltr"
                    placeholder="Dr. Abdullah Al-Saeed"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-base font-semibold">المنصب / Position</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="title-ar"
                    value={formData.title.ar}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                    dir="rtl"
                    placeholder="رئيس مجلس الإدارة"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="title-en"
                    value={formData.title.en}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                    dir="ltr"
                    placeholder="Chairman of the Board"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-base font-semibold">المؤهلات / Credentials</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="credentials-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="credentials-ar"
                    value={formData.credentials.ar}
                    onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, ar: e.target.value } })}
                    dir="rtl"
                    placeholder="دكتوراه في الصحة العامة - جامعة الملك سعود"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="credentials-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="credentials-en"
                    value={formData.credentials.en}
                    onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, en: e.target.value } })}
                    dir="ltr"
                    placeholder="PhD in Public Health - King Saud University"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="image">رابط الصورة</Label>
              <Input
                id="image"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="text-left"
                dir="ltr"
                placeholder="https://example.com/image.jpg"
              />
              {formData.image && (
                <img
                  src={formData.image}
                  alt="Preview"
                  className="w-32 h-32 rounded-full object-cover mt-2"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="order">الترتيب</Label>
              <Input
                id="order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="text-right"
                dir="rtl"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                إلغاء
              </Button>
              <Button onClick={handleSave}>
                حفظ التغييرات
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">إضافة عضو جديد</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label className="text-base font-semibold">الاسم / Name</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-name-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="add-name-ar"
                    value={formData.name.ar}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ar: e.target.value } })}
                    dir="rtl"
                    placeholder="د. عبدالله السعيد"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-name-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="add-name-en"
                    value={formData.name.en}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                    dir="ltr"
                    placeholder="Dr. Abdullah Al-Saeed"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-base font-semibold">المنصب / Position</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-title-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="add-title-ar"
                    value={formData.title.ar}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                    dir="rtl"
                    placeholder="رئيس مجلس الإدارة"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-title-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="add-title-en"
                    value={formData.title.en}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                    dir="ltr"
                    placeholder="Chairman of the Board"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-base font-semibold">المؤهلات / Credentials</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-credentials-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="add-credentials-ar"
                    value={formData.credentials.ar}
                    onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, ar: e.target.value } })}
                    dir="rtl"
                    placeholder="دكتوراه في الصحة العامة - جامعة الملك سعود"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-credentials-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="add-credentials-en"
                    value={formData.credentials.en}
                    onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, en: e.target.value } })}
                    dir="ltr"
                    placeholder="PhD in Public Health - King Saud University"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-image">رابط الصورة</Label>
              <Input
                id="add-image"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="text-left"
                dir="ltr"
                placeholder="https://example.com/image.jpg"
              />
              {formData.image && (
                <img
                  src={formData.image}
                  alt="Preview"
                  className="w-32 h-32 rounded-full object-cover mt-2"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-order">الترتيب</Label>
              <Input
                id="add-order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="text-right"
                dir="rtl"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
                إلغاء
              </Button>
              <Button onClick={handleAddMember}>
                إضافة العضو
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm !== null} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">تأكيد الحذف</DialogTitle>
          </DialogHeader>
          <p className="text-right py-4">هل أنت متأكد من حذف هذا العضو؟ لا يمكن التراجع عن هذا الإجراء.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              إلغاء
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => showDeleteConfirm && handleDelete(showDeleteConfirm)}
            >
              حذف
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

